/**
 * Phase F — Optimistic mutation hooks for the table state machine.
 *
 * Each mutation:
 *  - sends `If-Match` with the table's current `version`
 *  - on success: invalidates ['tables'] (and ['app_state'] when relevant)
 *  - on 409: shows toast "Table state changed — refreshing" + invalidates
 */
import { useMutation, useQueryClient, useIsMutating } from "@tanstack/react-query";
import { toast } from "sonner";
import useAppStore from "@/store/useAppStore";
import useSelectionStore from "@/store/useSelectionStore";
import { resyncTick } from "@/hooks/useTimerTick";
import {
  assignTable,
  bulkUpdateCategories,
  clearTable,
  closeTable,
  createDynamicTable,
  patchAppState,
  reassignTable,
  reserveFromOccupied,
  reserveTable,
  resetApp,
  startBreakfast,
} from "@/api/mutations";

/**
 * Phase G (Δ-11) — exposes the "any mutation in flight" boolean for
 * `useTables` to gate its polling on. Wraps TanStack Query's
 * `useIsMutating()` count. See CONSTRAINTS C-18.
 */
export function useIsAnyMutationPending() {
  return useIsMutating() > 0;
}

function handleError(error, queryClient) {
  const status = error?.response?.status;
  if (status === 409) {
    toast.error("Table state changed — refreshing");
    queryClient.invalidateQueries({ queryKey: ["tables"] });
    return;
  }
  const detail =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    "Request failed";
  toast.error(typeof detail === "string" ? detail : "Request failed");
}

function invalidateAll(queryClient) {
  queryClient.invalidateQueries({ queryKey: ["tables"] });
  // CR-0022 (Δ-21): bust every report query (prefix match across tab,
  // date range, and CR-0021 `type` filter) so the report screen
  // refreshes immediately after Assign / Reassign / ReserveFromOccupied.
  queryClient.invalidateQueries({ queryKey: ["report"] });
}

// ── State machine mutators ────────────────────────────────────────────────

export function useReserveTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload, version }) => reserveTable(code, payload, version),
    onSuccess: () => {
      invalidateAll(qc);
      // CR-0019 (Δ-18): phase-realign the global tick to the reservation
      // moment so the first "1s" appears at +1.0s, not at the next
      // ambient interval beat (could be up to +2.0s pre-fix).
      resyncTick();
    },
    onError: (e) => handleError(e, qc),
  });
}

export function useAssignTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload, version }) => assignTable(code, payload, version),
    onSuccess: () => invalidateAll(qc),
    onError: (e) => handleError(e, qc),
  });
}

export function useCloseTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload, version }) => closeTable(code, payload, version),
    onSuccess: () => invalidateAll(qc),
    onError: (e) => handleError(e, qc),
  });
}

export function useClearTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, version }) => clearTable(code, version),
    onSuccess: () => invalidateAll(qc),
    onError: (e) => handleError(e, qc),
  });
}

export function useReassignTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload, version }) => reassignTable(code, payload, version),
    onSuccess: () => {
      invalidateAll(qc);
      // CR-0019 (Δ-18): reassign restarts the waiting timer — same
      // phase-realign as Reserve so the new "0s → 1s" lands at +1.0s.
      resyncTick();
    },
    onError: (e) => handleError(e, qc),
  });
}

export function useReserveFromOccupied() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload, version }) =>
      reserveFromOccupied(code, payload, version),
    onSuccess: () => {
      invalidateAll(qc);
      // CR-0019 (Δ-18): occupied→reserved starts a fresh waiting timer.
      resyncTick();
    },
    onError: (e) => handleError(e, qc),
  });
}

// ── Dynamic table ─────────────────────────────────────────────────────────

export function useCreateDynamicTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name }) => createDynamicTable(name),
    onSuccess: () => invalidateAll(qc),
    onError: (e) => handleError(e, qc),
  });
}

// ── Categories bulk ───────────────────────────────────────────────────────

/**
 * CR-0011 Slice 6 — optimistic bulk category update with atomic rollback.
 *
 *  onMutate  → snapshot existing categories, apply optimistically,
 *              IMMEDIATELY clear the selection (closes the panel)
 *  onError   → rollback to snapshot + toast
 *  onSuccess → invalidate to reconcile with server truth
 */
export function useBulkUpdateCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codes, category, add }) => bulkUpdateCategories(codes, category, add),
    onMutate: ({ codes, category, add }) => {
      const { applyBulkCategories } = useAppStore.getState();
      const snapshot = applyBulkCategories(codes, category, add);
      // Auto-clear selection — panel auto-hides since it gates on selectedCodes.length > 0
      useSelectionStore.getState().clear();
      return { snapshot };
    },
    onError: (e, _vars, context) => {
      if (context?.snapshot) {
        useAppStore.getState().restoreCategoriesSnapshot(context.snapshot);
      }
      handleError(e, qc);
    },
    onSuccess: () => invalidateAll(qc),
  });
}

// ── App lifecycle ─────────────────────────────────────────────────────────

export function useStartBreakfast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => startBreakfast(),
    onSuccess: () => {
      // CR-0014c (Δ-13c, C-25 amendment to C-22): reset `selectedMode`
      // so the tabs that remount post-breakfast start in the neutral
      // "show everything" state.
      useAppStore.getState().setSelectedMode(null);
      qc.invalidateQueries({ queryKey: ["app_state"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    },
    onError: (e) => handleError(e, qc),
  });
}

export function useResetApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => resetApp(),
    onSuccess: () => {
      // CR-0014c (Δ-13c, C-25 amendment to C-22): clear `selectedMode`
      // on Reset so a previously-active tab does not silently re-appear
      // when tabs remount on the next breakfast cycle.
      useAppStore.getState().setSelectedMode(null);
      qc.invalidateQueries({ queryKey: ["app_state"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    },
    onError: (e) => handleError(e, qc),
  });
}

/**
 * Phase H (Δ-13) — toggle a single category in/out of
 * `active_category_filters` on app_state.
 *
 * CR-0014a polish (Δ-13a, 2026-05-30): now OPTIMISTIC. The store is
 * updated in `onMutate` before the network request fires, so the chip
 * UI changes instantly. On error the previous state is restored from
 * the `onMutate` context snapshot.
 *
 * Payload: `{ category: 'vip'|'email'|'complain', active: boolean }`
 *   - active = true  → add to filter set
 *   - active = false → remove from filter set
 */
export function useToggleCategoryFilter() {
  const qc = useQueryClient();
  return useMutation({
    onMutate: ({ category, active }) => {
      const state = useAppStore.getState();
      const previousState = state.appState;
      const current = previousState?.active_category_filters || [];
      const next = active
        ? current.includes(category)
          ? current
          : [...current, category]
        : current.filter((c) => c !== category);
      // Optimistic UI: chip flips state immediately.
      state.setAppState({
        ...previousState,
        active_category_filters: next,
      });
      return { previousState };
    },
    mutationFn: async () => {
      // The optimistic update has already happened — read the
      // post-update state and PATCH the server with it.
      const next =
        useAppStore.getState().appState?.active_category_filters || [];
      return await patchAppState({ active_category_filters: next });
    },
    onError: (e, _vars, context) => {
      // Rollback to the pre-click snapshot.
      if (context?.previousState) {
        useAppStore.getState().setAppState(context.previousState);
      }
      handleError(e, qc);
    },
    onSuccess: (data) => {
      // Server is the ultimate source of truth — replace local copy.
      if (data) useAppStore.getState().setAppState(data);
      qc.invalidateQueries({ queryKey: ["app_state"] });
    },
  });
}

